import React, { useEffect, useState } from "react";
import { database, ref, onValue, push, update, remove } from "./firebase";
import "./BillingSystem.css";
import Navigation from "./Navigation";

export default function BankPage() {
  const today = new Date().toISOString().split("T")[0];

  const [selectedDate, setSelectedDate] = useState(today);

  const [bottlers, setBottlers] = useState([]);
  const [enterprises, setEnterprises] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [deposits, setDeposits] = useState([]);

  /* 🔹 BANK INPUTS */
  const [accountName, setAccountName] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [depositedBy, setDepositedBy] = useState("");
  const [depositAmount, setDepositAmount] = useState("");
  const [editId, setEditId] = useState(null);

  /* 🔹 LOAD DATA */
  useEffect(() => {
    onValue(ref(database, "bottlersSPHistory"), snap =>
      setBottlers(snap.exists() ? Object.values(snap.val()) : [])
    );

    onValue(ref(database, "enterprisesSPHistory"), snap =>
      setEnterprises(snap.exists() ? Object.values(snap.val()) : [])
    );

    onValue(ref(database, "expensesHistory"), snap =>
      setExpenses(snap.exists() ? Object.values(snap.val()) : [])
    );

    onValue(ref(database, "bankDeposits"), snap => {
      if (!snap.exists()) {
        setDeposits([]);
        return;
      }
      const list = Object.keys(snap.val()).map(id => ({
        id,
        ...snap.val()[id],
      }));
      list.sort((a, b) => new Date(b.date) - new Date(a.date));
      setDeposits(list);
    });
  }, []);

  /* 🔹 SAFE DATE FILTER */
  const byDate = (arr) =>
    arr.filter(e => e.date && String(e.date).slice(0, 10) === selectedDate);

  const sum = (arr, key) =>
    arr.reduce((s, x) => s + Number(x[key] || 0), 0);

  /* 🔹 DAILY TOTALS */
  const bottlersCash = sum(byDate(bottlers), "cashAmount");
  const enterprisesCash = sum(byDate(enterprises), "cashAmount");
  const expensesCash = sum(byDate(expenses), "cashAmount");

  const depositedCash = sum(
    deposits.filter(d => d.date === selectedDate),
    "amount"
  );

  /* 🔹 CASH IN HAND */
  const availableCash =
    bottlersCash + enterprisesCash - expensesCash - depositedCash;

  /* 🔹 AUTO FILL */
  useEffect(() => {
    if (!editId) {
      setDepositAmount(availableCash > 0 ? availableCash : "");
    }
  }, [availableCash, editId]);

  /* 🔹 SAVE / UPDATE DEPOSIT */
  const saveDeposit = () => {
    if (!accountName || !depositAmount) {
      alert("Account name & amount required");
      return;
    }

    const record = {
      date: selectedDate,
      amount: Number(depositAmount),
      accountName,
      accountNumber,
      depositedBy,
      createdAt: Date.now(),
    };

    if (editId) {
      update(ref(database, `bankDeposits/${editId}`), record);
      alert("✅ Deposit updated");
    } else {
      push(ref(database, "bankDeposits"), record);
      alert("✅ Deposit saved");
    }

    resetForm();
  };

  const resetForm = () => {
    setAccountName("");
    setAccountNumber("");
    setDepositedBy("");
    setDepositAmount("");
    setEditId(null);
  };

  /* 🔹 EDIT */
  const handleEdit = (d) => {
    setEditId(d.id);
    setSelectedDate(d.date);
    setAccountName(d.accountName);
    setAccountNumber(d.accountNumber);
    setDepositedBy(d.depositedBy);
    setDepositAmount(d.amount);
  };

  /* 🔹 DELETE */
  const handleDelete = (id) => {
    if (window.confirm("Delete this deposit?")) {
      remove(ref(database, `bankDeposits/${id}`));
    }
  };

  return (
    <div className="history-container">
      <Navigation role="special manager" />

      <h2 className="page-title">🏦 Bank Page</h2>

      {/* DATE SELECT */}
      <div className="entry-controls">
        <input
          type="date"
          className="input-date"
          value={selectedDate}
          onChange={e => setSelectedDate(e.target.value)}
        />
      </div>

      {/* SUMMARY */}
      <table className="styled-table">
        <thead>
          <tr>
            <th>Source</th>
            <th>Cash (₹)</th>
          </tr>
        </thead>
        <tbody>
          <tr><td>Bottlers</td><td>₹{bottlersCash}</td></tr>
          <tr><td>Enterprises</td><td>₹{enterprisesCash}</td></tr>
          <tr><td>Expenses</td><td style={{color:"red"}}>-₹{expensesCash}</td></tr>
          <tr className="total-row">
            <td><b>Cash in Hand</b></td>
            <td><b>₹{availableCash}</b></td>
          </tr>
        </tbody>
      </table>

      {/* DEPOSIT FORM */}
      <h3 className="section-title">➕ Deposit to Bank</h3>

      <div className="bank-grid">
        <input className="input-text" placeholder="Account Name" value={accountName} onChange={e=>setAccountName(e.target.value)} />
        <input className="input-text" placeholder="Account Number" value={accountNumber} onChange={e=>setAccountNumber(e.target.value)} />
        <input className="input-text" placeholder="Deposited By" value={depositedBy} onChange={e=>setDepositedBy(e.target.value)} />
        <input type="number" className="input-small" placeholder="Amount ₹" value={depositAmount} onChange={e=>setDepositAmount(e.target.value)} />
        <button className="save-btn" onClick={saveDeposit}>
          {editId ? "✏️ Update" : "💾 Deposit"}
        </button>
      </div>

      {/* DEPOSIT HISTORY */}
      <h3 className="section-title">📜 Deposit History</h3>

      <div style={{ overflowX: "auto" }}>
        <table className="styled-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Account</th>
              <th>Account No</th>
              <th>Deposited By</th>
              <th>Amount (₹)</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {deposits.length === 0 ? (
              <tr><td colSpan="6">No deposits yet</td></tr>
            ) : (
              deposits.map(d => (
                <tr key={d.id}>
                  <td>{d.date}</td>
                  <td>{d.accountName}</td>
                  <td>{d.accountNumber || "—"}</td>
                  <td>{d.depositedBy || "—"}</td>
                  <td><b>₹{d.amount}</b></td>
                  <td>
                    <button className="edit-btn" onClick={()=>handleEdit(d)}>✏️ Edit</button>
                    <button className="delete-btn" onClick={()=>handleDelete(d.id)}>🗑️ Delete</button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
